import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { Observations } from '../../../types/prf';

interface ObservationsSectionProps {
  data: Observations;
  onSave: (data: Observations) => void;
  onBack: () => void;
}

type ObsTab = 'obs1' | 'obs2';

export default function ObservationsSection({
  data,
  onSave,
  onBack,
}: ObservationsSectionProps) {
  const [activeTab, setActiveTab] = useState<ObsTab>('obs1');
  const [obs1Data, setObs1Data] = useState<Observations>(data);
  const [obs2Data, setObs2Data] = useState<Observations>({});

  const handleObs1Change = (field: keyof Observations, value: string | number) => {
    setObs1Data(prev => ({ ...prev, [field]: value }));
  };

  const handleObs2Change = (field: keyof Observations, value: string | number) => {
    setObs2Data(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Combine both OBS readings into the data structure
    onSave({
      ...obs1Data,
      additionalNotes: JSON.stringify({ obs1: obs1Data, obs2: obs2Data }),
    });
  };

  const renderObsForm = (obsNumber: 1 | 2) => {
    const currentData = obsNumber === 1 ? obs1Data : obs2Data;
    const handleChange = obsNumber === 1 ? handleObs1Change : handleObs2Change;

    return (
      <div className="space-y-4">
        {/* Date and Time */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            OBS{obsNumber} date and time
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              value={currentData.timeRecorded?.split('T')[0] || ''}
              onChange={(e) => handleChange('timeRecorded', e.target.value)}
              className="flex-1 px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            />
            <input
              type="time"
              value={currentData.timeRecorded?.split('T')[1]?.substring(0, 5) || ''}
              onChange={(e) => {
                const date = currentData.timeRecorded?.split('T')[0] || new Date().toISOString().split('T')[0];
                handleChange('timeRecorded', `${date}T${e.target.value}`);
              }}
              className="w-28 px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            />
          </div>
        </div>

        {/* Resp Rate & Sats % */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Resp Rate ({obsNumber})
            </label>
            <input
              type="number"
              value={currentData.respiratoryRate || ''}
              onChange={(e) => handleChange('respiratoryRate', parseInt(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="bpm"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Sats % A/O2 ({obsNumber})
            </label>
            <input
              type="number"
              value={currentData.oxygenSaturation || ''}
              onChange={(e) => handleChange('oxygenSaturation', parseInt(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="%"
            />
          </div>
        </div>

        {/* Pulse Rate */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Pulse Rate ({obsNumber})
          </label>
          <input
            type="number"
            value={currentData.heartRate || ''}
            onChange={(e) => handleChange('heartRate', parseInt(e.target.value))}
            className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            placeholder="bpm"
          />
        </div>

        {/* BP & Temp */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              BP ({obsNumber})
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                value={currentData.bloodPressureSystolic || ''}
                onChange={(e) => handleChange('bloodPressureSystolic', parseInt(e.target.value))}
                className="flex-1 min-w-0 px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                placeholder="Sys"
              />
              <span className="text-foreground flex-shrink-0">/</span>
              <input
                type="number"
                value={currentData.bloodPressureDiastolic || ''}
                onChange={(e) => handleChange('bloodPressureDiastolic', parseInt(e.target.value))}
                className="flex-1 min-w-0 px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                placeholder="Dia"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Temp ({obsNumber})
            </label>
            <input
              type="number"
              step="0.1"
              value={currentData.temperature || ''}
              onChange={(e) => handleChange('temperature', parseFloat(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="°C"
            />
          </div>
        </div>

        {/* Pain Score & Glucose Level */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Pain Score ({obsNumber})
            </label>
            <input
              type="number"
              min="0"
              max="10"
              value={currentData.painScore || ''}
              onChange={(e) => handleChange('painScore', parseInt(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="0-10"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Glucose Level ({obsNumber})
            </label>
            <input
              type="number"
              value={currentData.glucoseLevel || ''}
              onChange={(e) => handleChange('glucoseLevel', parseFloat(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="mmol/L"
            />
          </div>
        </div>

        {/* Consciousness */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Consciousness ({obsNumber})
          </label>
          <input
            type="text"
            value={currentData.consciousness || ''}
            onChange={(e) => handleChange('consciousness', e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            placeholder="e.g., Alert, Voice, Pain, Unresponsive (AVPU)"
          />
        </div>

        {/* Additional Notes */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Additional Notes ({obsNumber})
          </label>
          <textarea
            value={currentData.additionalNotes || ''}
            onChange={(e) => handleChange('additionalNotes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
            placeholder="Any additional observation notes..."
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">OBS</h2>
              <p className="text-xs text-muted-foreground">Patient observations and vital signs</p>
            </div>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex border-t border-border">
          <button
            onClick={() => setActiveTab('obs1')}
            className={cn(
              'flex-1 px-4 py-3 text-sm font-semibold transition-colors',
              activeTab === 'obs1'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            )}
          >
            OBS 1
          </button>
          <button
            onClick={() => setActiveTab('obs2')}
            className={cn(
              'flex-1 px-4 py-3 text-sm font-semibold transition-colors',
              activeTab === 'obs2'
                ? 'bg-muted-foreground text-background'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            )}
          >
            OBS 2
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          {activeTab === 'obs1' ? renderObsForm(1) : renderObsForm(2)}
        </div>
      </div>

      {/* Footer with Save Button */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSave}
          className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
}
